import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { VisitorPlace } from '../../models/visitor.place';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { SharedModule } from '../../shared.module';
import { CommonModule, NgIf } from '@angular/common';
import { TripPlanListComponent } from 'src/app/modules/trip-planner/components/trip.planner/trip.plan.list/trip.plan.list.component';
import { clone } from 'src/app/common/utils/object.extensions';
import { VisitorService } from '../../services/visitor.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CreateEditPlanComponent } from 'src/app/modules/trip-planner/components/trip.planner/create.edit.plan/create.edit.plan.component';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTabGroup } from '@angular/material/tabs';
import { TripPlan } from '../../models/vititor.trip.storage.command';
import { MatMenuTrigger } from '@angular/material/menu';
import { ConfirmDialogComponent } from 'src/app/common/components/confirm.dialog/confirm.dialog.component';
import { SearchAttractionsComponent } from 'src/app/modules/trip-planner/components/itinerary/search.attractions/search.attractions.component';
import { ExportPDFService, ExportTripPDFCommand, PDFType } from '../../services/export.pdf.service';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { GoogleMapService } from '../../services/google.map.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DirectionNavigationComponent } from 'src/app/modules/trip-planner/components/itinerary/direction.navigation/direction.navigation.component';
import { VisitorEvent } from '../../models/visitor.event';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import {
  ShareItineraryModalComponent,
  ShareItineraryModalData,
} from 'src/app/modules/trip-planner/components/common/share.itinerary.modal/share.itinerary.modal.component';
import { finalize, of } from 'rxjs';
import { IMapMarkerWithCustomData } from '../../models/place.marker';
import { GoogleMapComponent } from '../google.map/google.map.component';

@Component({
  selector: 'ig-trip-editor',
  templateUrl: './trip.editor.component.html',
  styleUrl: './trip.editor.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SharedModule,
    CommonModule,
    TripPlanListComponent,
    CreateEditPlanComponent,
    CdkDrag,
    CdkDropList,
    NgIf,
    ConfirmDialogComponent,
    SearchAttractionsComponent,
    MatProgressSpinnerModule,
    DirectionNavigationComponent,
  ],
})
export class TripEditorComponent implements OnChanges, AfterViewInit {
  @Input() currentTrip: any = null;
  @Input() userEntireTripId: string = null;
  @Input() canEditTrip: boolean = true;
  @Input() canSaveMoreTrips: boolean = true;
  @Input() hubName: string = '';
  @Input() cityName: string = '';
  @Input() userInfo: {
    userId: string;
    avatar: string;
    userName: string;
  } = null;
  isEditMode = false;
  isDragging = false;
  titleForm: FormGroup;
  initTripBeforeEditing: any = null;
  isShowTripFunctions = true;
  deleteDayIndex = 0;
  dayAttractionLimit = 10;
  isShowDeleteAttractionConfirms = [];
  pdfProgress: number = 0;
  isMobile = false;
  isShowMap = false;
  mapMarkers = [];
  isShowGPSConfirm = false;
  selectedDayIndex = 0;

  @Output() onCloseDetailsPanel = new EventEmitter<void>();
  @Output() onClickAttraction = new EventEmitter<any>();
  @Output() onCopyTrip = new EventEmitter<any>();
  @Output() onRequireLogin = new EventEmitter<{ action: 'copyTrip'; trip: any }>();
  @ViewChild('daysTabGroup', { static: false }) daysTabGroup: MatTabGroup;
  @ViewChild(GoogleMapComponent, { static: false })
  googleMap: GoogleMapComponent;

  visitorService = inject(VisitorService);
  snackBar = inject(MatSnackBar);
  googleMapService = inject(GoogleMapService);
  pdfService = inject(ExportPDFService);
  modalService = inject(ModalService);

  constructor(
    public mediaQueryService: MediaQueryService,
    private _fb: FormBuilder,
    private visitorPlaceService: VisitorPlaceService,
  ) {
    this.mediaQueryService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.titleForm = this._fb.group({
      title: ['', Validators.required],
      date: [''],
      note: [''],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentTrip'] || changes['canEditTrip']) {
      this.normalizeSelectedDayIndex();
      this.renderSharedTripDesktopMap();
    }
  }

  ngAfterViewInit(): void {
    this.renderSharedTripDesktopMap();
  }

  get isSharedTripDesktopLayout(): boolean {
    return !this.canEditTrip && !this.isMobile;
  }

  get showTripInfoPane(): boolean {
    return !this.isShowMap || this.isSharedTripDesktopLayout;
  }

  backToTripList() {}
  closeDetailsPanel() {
    this.onCloseDetailsPanel.emit();
  }

  clickAttraction(attraction) {
    this.onClickAttraction.emit(attraction.item);
  }

  editOrSave($event?: any) {
    this.isEditMode = !this.isEditMode;

    if (this.isEditMode) {
      this.initTripBeforeEditing = clone(this.currentTrip);
      this.titleForm.get('title').setValue(this.initTripBeforeEditing.title);
    }

    if (!this.isEditMode) {
      this.saveTrip();
    }
  }

  cancelEdit() {
    this.isEditMode = false;
    this.currentTrip = this.initTripBeforeEditing;
  }

  showSuccessMessage(msg: string, duration: number = 3000) {
    this.snackBar.open(msg, 'OK', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['ig-success-snackbar'],
      duration: duration,
    });
  }

  addDay() {
    this.currentTrip.days.push({
      date: null,
      dateText: 'Day ' + (this.currentTrip.days.length + 1),
      note: '',
      attractions: [],
    });

    this.selectedDayIndex = this.currentTrip.days.length - 1;
    setTimeout(() => {
      if (this.daysTabGroup) {
        this.daysTabGroup.selectedIndex = this.selectedDayIndex;
      }
    });
  }

  editPlan(
    day: any,
    attractionIndex: number,
    planIndex: number,
    type: 'prePlans' | 'postPlans',
    $event: TripPlan,
  ) {
    day.attractions[attractionIndex][type][planIndex] = $event;
  }

  deletePlan(
    day: any,
    attractionIndex: number,
    planIndex: number,
    type: 'prePlans' | 'postPlans' = 'prePlans',
  ) {
    day.attractions[attractionIndex][type].splice(planIndex, 1);
  }

  swapPlan(
    day: any,
    attractionIndex: number,
    planIndex: number,
    type: 'prePlans' | 'postPlans' = 'prePlans',
  ) {
    const upperOne = day.attractions[attractionIndex][type][planIndex];
    day.attractions[attractionIndex][type][planIndex] =
      day.attractions[attractionIndex][type][planIndex + 1];
    day.attractions[attractionIndex][type][planIndex + 1] = upperOne;
  }

  switchDays(index: number) {
    this.selectedDayIndex = index ?? 0;
    this.normalizeSelectedDayIndex();
    this.renderSharedTripDesktopMap();
  }

  moveDayLeft(dayIndex: number) {
    if (dayIndex <= 0) return;

    const prev = this.currentTrip.days[dayIndex - 1];
    this.currentTrip.days[dayIndex - 1] = this.currentTrip.days[dayIndex];
    this.currentTrip.days[dayIndex] = prev;

    this.selectedDayIndex = dayIndex - 1;
    if (this.daysTabGroup) {
      this.daysTabGroup.selectedIndex = this.selectedDayIndex;
    }
  }

  moveDayRight(dayIndex: number) {
    if (dayIndex >= this.currentTrip.days.length - 1) return;

    const next = this.currentTrip.days[dayIndex + 1];
    this.currentTrip.days[dayIndex + 1] = this.currentTrip.days[dayIndex];
    this.currentTrip.days[dayIndex] = next;

    this.selectedDayIndex = dayIndex + 1;
    if (this.daysTabGroup) {
      this.daysTabGroup.selectedIndex = this.selectedDayIndex;
    }
  }

  onAttractionDrop(event, day) {
    // swap pre and post plans
    const prePlansPrev = day.attractions[event.previousIndex].prePlans;
    const postPlansPrev = day.attractions[event.previousIndex].postPlans;
    const prePlansCurr = day.attractions[event.currentIndex].prePlans;
    const postPlansCurr = day.attractions[event.currentIndex].postPlans;

    day.attractions[event.previousIndex].prePlans = prePlansCurr;
    day.attractions[event.previousIndex].postPlans = postPlansCurr;

    day.attractions[event.currentIndex].prePlans = prePlansPrev;
    day.attractions[event.currentIndex].postPlans = postPlansPrev;

    moveItemInArray(day.attractions, event.previousIndex, event.currentIndex);
  }

  setTripTitle(trigger: MatMenuTrigger) {
    this.currentTrip.title = this.titleForm.get('title').value;
    trigger.closeMenu();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  deleteAttraction(day, attractionIndex: number) {
    this.rebuildPlanAssociation(day, attractionIndex);
    day.attractions.splice(attractionIndex, 1);
    this.isShowDeleteAttractionConfirms[attractionIndex] = false;
  }

  rebuildPlanAssociation(day, attractionIndex) {
    const attraction = day.attractions[attractionIndex];
    if (!attraction.prePlans) attraction.prePlans = [];
    if (!attraction.postPlans) attraction.postPlans = [];

    // process the moving attraction
    // not last attraction (which only has pre-plans), move its pre-plans to the below attraction
    if (attractionIndex !== day.attractions.length - 1 && attraction.prePlans.length) {
      day.attractions[attractionIndex + 1].prePlans = [
        ...attraction.prePlans,
        ...day.attractions[attractionIndex + 1].prePlans,
      ];
      attraction.prePlans = [];
    }

    // last attraction, move its pre/post-plans to the above attraction
    if (attractionIndex === day.attractions.length - 1 && day.attractions.length > 1) {
      if (!day.attractions[attractionIndex - 1].postPlans) {
        day.attractions[attractionIndex - 1].postPlans = [];
      }
      day.attractions[attractionIndex - 1].postPlans = [
        ...day.attractions[attractionIndex - 1].postPlans,
        ...attraction.prePlans,
        ...attraction.postPlans,
      ];
      attraction.prePlans = [];
      attraction.postPlans = [];
    }

    // Moving the only one attraction in the day will remove all the plans within this day.
    if (attractionIndex === day.attractions.length - 1 && day.attractions.length === 1) {
      attraction.prePlans = [];
      attraction.postPlans = [];
    }
  }

  getCategoryText(place: VisitorPlace) {
    if (place.categoryText) {
      return place.categoryText;
    }

    if (place.category) {
      return this.visitorPlaceService.getDisplayCategory(place);
    }

    return '';
  }

  editDay(day, trigger: MatMenuTrigger) {
    day.dateText = this.titleForm.get('date').value;
    trigger.closeMenu();
  }

  onEditDayMenuOpened(day: any) {
    this.titleForm.get('date').setValue(day.dateText);
  }

  editDayNote(day, trigger) {
    day.note = this.titleForm.get('note').value;
    trigger.closeMenu();
  }

  showDeleteDayConfirm(dayIndex: number) {
    setTimeout(() => {
      this.deleteDayIndex = dayIndex;
    }, 0);
  }

  closeDeleteDayConfirm() {
    this.deleteDayIndex = null;
  }

  closeConfirms() {
    // this.isShowDeleteTripConfirms = [];
  }

  deleteDay(dayIndex: number) {
    this.currentTrip.days.splice(dayIndex, 1);
    this.deleteDayIndex = null;
    this.normalizeSelectedDayIndex();
  }

  addPlace(item, dayIndex: number) {
    console.log(item);

    if (this.currentTrip.days[dayIndex].attractions.some((x) => x.feedId === item._id)) {
      return;
    }

    if (!this.currentTrip.days || !this.currentTrip.days.length) {
      this.currentTrip.days = [
        {
          date: null,
          dateText: 'Day 1',
          note: '',
          attractions: [],
        },
      ];
    }

    this.currentTrip.days[dayIndex].attractions.push({
      feedType: item.startTime ? 'event' : 'place',
      feedId: item._id,
      status: 'ok',
      order: this.currentTrip.days[dayIndex].attractions.length,
      item,
    });
  }

  async exportPDF() {
    // this.isShowTripFunctions = false;

    let command = new ExportTripPDFCommand();
    command.hubName = this.hubName;
    command.type = PDFType.trip;
    command.trip = this.currentTrip;
    command.title = this.currentTrip.title;
    this.generateShareLink();

    command.progressCallback = (progress) => {
      this.pdfProgress = progress;
    };

    try {
      // for mobile, if Google map is not loaded, we need to load it before exporting. Otherwise, it will not generate the map
      if (this.isMobile) {
        this.googleMapService.loadGoogleMapApi().subscribe(async () => {
          await this.pdfService.exportTripPDF(command);
          this.pdfProgress = 0;
          this.removeShareLink();
        });
      } else {
        await this.pdfService.exportTripPDF(command);
        this.removeShareLink();
      }
    } catch (e) {
      alert(e.message);
      console.log(e);
    } finally {
      this.pdfProgress = 0;
      this.removeShareLink();
    }
  }

  travelMode(trigger: MatMenuTrigger) {
    // this.directionNavigation?.exit();
    // this.setMapMarkers();
    setTimeout(() => {
      trigger?.closeMenu();
      // this.isShowTripFunctions = false;
    }, 500);
  }

  checkHasAttractions() {
    if (this.currentTrip.days[this.daysTabGroup.selectedIndex].attractions.length > 0) {
      // this.isShowGPSConfirm = true;
      return;
    }

    this.snackBar.open('Please select at least one attraction', 'Close', {
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'ig-error-snackbar',
    });
  }

  gpsDirections() {
    // if (this.isMobile) this.showMobileMap(this.daysTabGroup.selectedIndex);
    // this.isNavigationShowing = true;
    // this.isShowGPSConfirm = false;
    // this.isShowTripFunctions = false;
    // setTimeout(() => {
    //   this.directionNavigation.navigateFromUserGPSLocation(this.currentTrip.travelMode);
    //   // back to trip attraction list
    //   if (this.isMobile) this.isShowingMapOnMobile = false;
    //   this.isNavigationShowing = false;
    // }, 50);
  }

  directions() {
    // if (this.isMobile) this.showMobileMap(this.daysTabGroup.selectedIndex);
    // this.isNavigationShowing = true;
    // this.isShowGPSConfirm = false;
    // this.isShowTripFunctions = false;
    // setTimeout(
    //   () => {
    //     this.directionNavigation.nextLeg();
    //   },
    //   this.isMobile ? 1500 : 100
    // );
  }

  markerClick($event) {
    this.googleMap.showEventInfoWindow();
    this.googleMap.showPlaceInfoWindow();
  }

  infoWindowClick($event) {
    const item = $event.marker.data as VisitorPlace | VisitorEvent;
    // this.onClickAttraction.emit(item);
  }

  onDetailsWindowClose($event) {}

  setMapMarkersForItems(items: (VisitorPlace | VisitorEvent)[]) {
    this.mapMarkers = [];
    if (this.googleMap) {
      this.googleMap.directionsResults$ = of(null);
    }

    const directionLocations: { lat: number; lng: number; stopover: boolean }[] = [];

    for (const place of items || []) {
      const lat = place?.address?.lat ?? place?.lat;
      const lng = place?.address?.lng ?? place?.lng;
      if (lat == null || lng == null) continue;

      this.mapMarkers.push({
        position: {
          lat,
          lng,
        },
        label: {
          text: '🏛 ' + place.name,
          fontWeight: 'normal',
          className:
            'bg-white text-black px-2 py-1 border border-black rounded shadow-lg -translate-y-6 z-0 relative',
        },
        options: {
          // icon: {
          //   url: `https://iti-images.s3.amazonaws.com/imgs/map-icon.png`,
          //   scaledSize: new google.maps.Size(40, 55),
          // },
          zIndex: 2,
        },
        title: place._id || place['feedId'] || place['id'], // use this for showing map info window when clicking on list items
        data: place,
      } as IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>);

      directionLocations.push({
        lat,
        lng,
        stopover: true,
      });
    }

    if (directionLocations.length <= 1) {
      if (this.googleMap) {
        this.googleMap.directionsResults$ = of(null);
      }
      return;
    }

    this.drawDirectionRouteForMarkers(directionLocations);
  }

  closeMap() {
    this.normalizeSelectedDayIndex();
    this.isShowMap = false;
    this.mapMarkers = [];
    if (this.googleMap) {
      this.googleMap.directionsResults$ = of(null);
    }
  }

  showMobileMap(item: number | VisitorPlace | VisitorEvent) {
    this.isShowMap = true;
    const activeDayIndex =
      typeof item === 'number'
        ? item
        : (this.daysTabGroup?.selectedIndex ?? this.selectedDayIndex ?? 0);
    this.selectedDayIndex = activeDayIndex;
    this.normalizeSelectedDayIndex();
    const items = this.getCurrentDayMapItems();

    requestAnimationFrame(() => {
      this.setMapMarkersForItems(items);
      requestAnimationFrame(() => {
        this.googleMap?.fitBounds();
      });
    });
  }

  private renderSharedTripDesktopMap(): void {
    if (!this.isSharedTripDesktopLayout) return;

    const items = this.getCurrentDayMapItems();
    requestAnimationFrame(() => {
      this.setMapMarkersForItems(items);
      requestAnimationFrame(() => {
        this.googleMap?.fitBounds();
      });
    });
  }

  private getCurrentDayMapItems(): (VisitorPlace | VisitorEvent)[] {
    const selectedDay = this.currentTrip?.days?.[this.selectedDayIndex];
    const attractions = this.getAvailableAttractions(selectedDay?.attractions || [])
      .map((attraction, index) => ({
        ...attraction,
        _order: attraction?.order ?? index,
      }))
      .sort((a, b) => a._order - b._order);

    return attractions.map((attraction) => attraction?.item).filter((attraction) => !!attraction);
  }

  private drawDirectionRouteForMarkers(
    locations: { lat: number; lng: number; stopover?: boolean }[],
    retryCount = 0,
  ): void {
    if (locations.length <= 1) {
      if (this.googleMap) {
        this.googleMap.directionsResults$ = of(null);
      }
      return;
    }

    // The map view is created via *ngIf; wait briefly until GoogleMapComponent is ready.
    if (!this.googleMap?.googleMap?.googleMap) {
      if (retryCount >= 10) return;
      setTimeout(() => this.drawDirectionRouteForMarkers(locations, retryCount + 1), 300);
      return;
    }

    const travelMode = (this.currentTrip?.travelMode ||
      google.maps.TravelMode.DRIVING) as google.maps.TravelMode;
    this.googleMap.drawDirectionRoute(locations, travelMode);
  }

  private normalizeSelectedDayIndex(): void {
    const dayCount = this.currentTrip?.days?.length || 0;
    if (!dayCount) {
      this.selectedDayIndex = 0;
      return;
    }

    if (this.selectedDayIndex < 0) {
      this.selectedDayIndex = 0;
      return;
    }

    if (this.selectedDayIndex > dayCount - 1) {
      this.selectedDayIndex = dayCount - 1;
    }
  }

  generateShareLink() {
    const url = new URL(window.location.href);
    url.searchParams.set('user-trip', this.userEntireTripId);
    url.searchParams.set('single-trip', this.currentTrip?._id || this.currentTrip?.id || '');
    window.history.replaceState({}, '', url.toString());
  }

  removeShareLink() {
    // remove user trip from url
    const url = new URL(window.location.href);
    url.searchParams.delete('user-trip');
    url.searchParams.delete('single-trip');
    window.history.replaceState({}, '', url.toString());
  }

  shareTrip() {
    if (!this.userInfo?.userId) {
      this.snackBar.open('Please login to share your trip.', 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: 'ig-error-snackbar',
      });
      return;
    }

    this.generateShareLink();

    const showShareModel = () => {
      const modal = this.modalService.show({
        component: ShareItineraryModalComponent,
        data: {
          itinerary: this.currentTrip,
          hubName: this.hubName,
          cityName: this.cityName || this.hubName,
          isEditMode: false,
          type: 'ai-trip',
          user: {
            profilePicture: this.userInfo.avatar,
            userName: this.userInfo.userName?.replace('|google', '') || '',
          },
        } as ShareItineraryModalData,
      });

      modal.beforeClosed().subscribe((data) => {
        this.removeShareLink();
      });
    };

    showShareModel();
  }

  saveTrip() {
    if (this.userInfo?.userId) {
      this.visitorService
        .saveTrip(this.hubName, this.currentTrip, this.userInfo.userId)
        .subscribe(() => {
          this.showSuccessMessage('Trip saved successfully');
        });
    }
  }

  copyTrip() {
    if (!this.canSaveMoreTrips) {
      this.snackBar.open('You have reached the maximum number of saved trips', 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: 'ig-error-snackbar',
      });
      return;
    }

    if (!this.userInfo?.userId) {
      this.onRequireLogin.emit({ action: 'copyTrip', trip: this.currentTrip });
      return;
    }

    this.onCopyTrip.emit(this.currentTrip);
  }

  getAvailableAttractions(attractions: any[]) {
    if (!Array.isArray(attractions)) return [];
    return attractions.filter((x) => {
      if (!x?.item) return false;
      if (!x.status) return true;
      return x.status.toLowerCase() === 'ok';
    });
  }
}
